/**
 * Billing gate + post-checkout feedback for tenant billing (`isBillingSetUp`).
 *
 * Paths:
 * 1) Billing not set up, no `paymentMethodUpdateResult` query param → blocking lock modal
 *    (“activate subscription” → checkout on api).
 * 2) `paymentMethodUpdateResult=success` (e.g. return from checkout) → success modal instead of
 *    the lock. User taps OK → invalidate/refetch tenant query, then poll every ~2s up to 120s for
 *    `isBillingSetUp`. When true → clear the query param (modal closes; lock hidden because billing
 *    is active). If 120s elapses without billing active → set param to `failure` (see path 3).
 * 3) `paymentMethodUpdateResult=failure` (checkout cancelled, backend redirect, or timeout from
 *    path 2) → failure modal. “Try again” clears the query param → path 1 (lock) shows again.
 *
 * If billing becomes active while a result param is still present (e.g. race), an effect clears
 * the param so the URL stays clean.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CircleCheck, TriangleAlert, XCircle } from 'lucide-react';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { useCallback, useEffect, useState } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { tenantQueryKey, useTenant } from '@/hooks/use-tenant';
import { API_BASE_URL } from '@/lib/api-client';
import type { TenantDto } from '@/generated/korza-api-client/types.gen';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import z from 'zod';
import { LoaderButton } from './loader-button';

const PAYMENT_METHOD_UPDATE_RESULTS = ['success', 'failure'] as const;

type TenantQueryData = { slug: string; tenant: TenantDto | null };

function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function BillingLockModal() {
  const tenant = useTenant();
  const { LL } = useI18nContext();
  const queryClient = useQueryClient();
  const { data: featureFlags, isLoading: featureFlagsLoading } =
    useFeatureFlags();
  const customPaymentLink = featureFlags?.customPaymentLink?.trim();
  const [paymentMethodUpdateResult, setPaymentMethodUpdateResult] =
    useQueryState(
      'paymentMethodUpdateResult',
      parseAsStringLiteral(PAYMENT_METHOD_UPDATE_RESULTS)
    );
  const [confirmingBillingAfterSuccess, setConfirmingBillingAfterSuccess] =
    useState(false);

  const checkoutMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(
        `${API_BASE_URL}/tenants/${tenant.slug}/billing/pay`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create billing checkout session');
      }

      const json = await response.json();
      return z.object({ checkoutUrl: z.string() }).parse(json).checkoutUrl;
    },
    onSuccess: (checkoutUrl) => {
      window.location.replace(checkoutUrl);
    },
  });

  const readIsBillingSetUpFromCache = useCallback(() => {
    const row = queryClient.getQueryData<TenantQueryData>(
      tenantQueryKey(tenant.slug)
    );
    return row?.tenant?.isBillingSetUp === true;
  }, [queryClient, tenant.slug]);

  const handleSuccessOk = useCallback(async () => {
    const qk = tenantQueryKey(tenant.slug);
    setConfirmingBillingAfterSuccess(true);
    try {
      if (readIsBillingSetUpFromCache()) {
        await setPaymentMethodUpdateResult(null);
        return;
      }

      await queryClient.invalidateQueries({ queryKey: qk });
      await queryClient.refetchQueries({ queryKey: qk });

      if (readIsBillingSetUpFromCache()) {
        await setPaymentMethodUpdateResult(null);
        return;
      }

      const deadline = Date.now() + 120_000;
      while (Date.now() < deadline) {
        await sleep(2000);
        await queryClient.refetchQueries({ queryKey: qk });
        if (readIsBillingSetUpFromCache()) {
          await setPaymentMethodUpdateResult(null);
          return;
        }
      }

      await setPaymentMethodUpdateResult('failure');
    } finally {
      setConfirmingBillingAfterSuccess(false);
    }
  }, [
    queryClient,
    readIsBillingSetUpFromCache,
    setPaymentMethodUpdateResult,
    tenant.slug,
  ]);

  useEffect(() => {
    // Hide the failure modal if the tenant loads up with isBillingSetUp as true
    // Don't do this for the success modal because we want the user to see the success message
    if (paymentMethodUpdateResult === 'failure' && tenant.isBillingSetUp) {
      void setPaymentMethodUpdateResult(null);
    }
  }, [
    paymentMethodUpdateResult,
    tenant.isBillingSetUp,
    setPaymentMethodUpdateResult,
  ]);

  if (paymentMethodUpdateResult === 'success') {
    return (
      <AlertDialog open>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="mb-2 flex justify-center">
              <span className="rounded-full bg-emerald-100 p-3 text-emerald-700">
                <CircleCheck className="size-6" />
              </span>
            </div>
            <AlertDialogTitle className="text-center text-xl">
              {LL.billingLockModal.paymentMethodUpdate.successTitle()}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              {LL.billingLockModal.paymentMethodUpdate.successDescription()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <LoaderButton
              className="w-full"
              onClick={() => void handleSuccessOk()}
              disabled={confirmingBillingAfterSuccess}
              isLoading={confirmingBillingAfterSuccess}
            >
              {LL.billingLockModal.paymentMethodUpdate.successOkButton()}
            </LoaderButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  if (paymentMethodUpdateResult === 'failure') {
    return (
      <AlertDialog open>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="mb-2 flex justify-center">
              <span className="rounded-full bg-destructive/15 p-3 text-destructive">
                <XCircle className="size-6" />
              </span>
            </div>
            <AlertDialogTitle className="text-center text-xl">
              {LL.billingLockModal.paymentMethodUpdate.failureTitle()}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              {LL.billingLockModal.paymentMethodUpdate.failureDescription({
                supportEmail: import.meta.env.VITE_PUBLIC_CONTACT_EMAIL,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              className="w-full"
              onClick={() => void setPaymentMethodUpdateResult(null)}
            >
              {LL.billingLockModal.paymentMethodUpdate.failureTryAgainButton()}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  if (!featureFlagsLoading && customPaymentLink) {
    return null;
  }

  if (tenant.isBillingSetUp) {
    return null;
  }

  return (
    <AlertDialog open>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="mb-2 flex justify-center">
            <span className="rounded-full bg-yellow-100 p-3 text-yellow-700">
              <TriangleAlert className="size-6" />
            </span>
          </div>
          <AlertDialogTitle className="text-center text-xl">
            {LL.billingLockModal.title()}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center">
            {LL.billingLockModal.description()}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <LoaderButton
            className="w-full"
            onClick={() => checkoutMutation.mutate()}
            disabled={checkoutMutation.isPending}
            isLoading={checkoutMutation.isPending}
          >
            {LL.billingLockModal.activateSubscriptionButton()}
          </LoaderButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
